import type { ReactNode } from "react";
import styles from "./Table.module.css";
import { Skeleton } from "./Skeleton";

export interface TableColumn<Row> {
  key: string;
  label: string;
  /** Render one cell. Defaults to reading row[key] as a string. */
  render?: (row: Row) => ReactNode;
  width?: number | string;
  align?: "left" | "right";
  sortable?: boolean;
  /** Style hint — muted cells use `--text-secondary`. */
  muted?: boolean;
}

interface TableProps<Row> {
  columns: readonly TableColumn<Row>[];
  rows: readonly Row[];
  /** Row identifier extractor. Required for React keys. */
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  onSortChange?: (key: string, dir: "asc" | "desc") => void;
  loading?: boolean;
  /**
   * Rendered when `rows` is empty. Callers MUST distinguish
   * "no data at all" from "filtered to zero" and pass the appropriate
   * ReactNode here — same prop, different content.
   */
  empty?: ReactNode;
  stickyHeader?: boolean;
  /** Number of skeleton rows to render while `loading`. */
  loadingRowCount?: number;
}

/**
 * Table — the workhorse for Activity, Statements, holdings.
 *
 * Sticky header by default (per §5 Activity). Amount columns right-
 * align with tabular figures. `empty` and filtered-to-zero are the
 * caller's distinction; this component just renders whatever the
 * caller passes as `empty`. See EmptyState.tsx for the copy convention.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  sortBy,
  sortDir,
  onSortChange,
  loading = false,
  empty,
  loadingRowCount = 6,
}: TableProps<Row>) {
  const handleHeaderClick = (col: TableColumn<Row>) => {
    if (!col.sortable || !onSortChange) return;
    const nextDir: "asc" | "desc" =
      sortBy === col.key && sortDir === "asc" ? "desc" : "asc";
    onSortChange(col.key, nextDir);
  };

  return (
    <div className={styles.wrap}>
      <table className={styles.table} role="table">
        <thead>
          <tr>
            {columns.map((col) => {
              const isSortable = col.sortable && !!onSortChange;
              const isActive = sortBy === col.key;
              const cellClass = [
                styles.headerCell,
                isSortable ? styles.headerCellSortable : null,
                col.align === "right" ? styles.headerCellRight : null,
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <th
                  key={col.key}
                  className={cellClass}
                  style={col.width ? { width: col.width } : undefined}
                  scope="col"
                  onClick={isSortable ? () => handleHeaderClick(col) : undefined}
                  aria-sort={
                    isActive
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : isSortable
                        ? "none"
                        : undefined
                  }
                >
                  {col.label}
                  {isActive ? (
                    <span aria-hidden className={styles.sortIndicator}>
                      {sortDir === "asc" ? "▲" : "▼"}
                    </span>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: loadingRowCount }).map((_, i) => (
              <tr key={`skel-${i}`} className={styles.row}>
                {columns.map((col) => (
                  <td key={col.key} className={styles.cell}>
                    <Skeleton variant="row" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr className={styles.emptyRow}>
              <td colSpan={columns.length}>{empty}</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={`${styles.row} ${onRowClick ? styles.rowClickable : ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => {
                  const cellClass = [
                    styles.cell,
                    col.align === "right" ? styles.cellRight : null,
                    col.muted ? styles.cellSecondary : null,
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <td key={col.key} className={cellClass}>
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? "")}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
